import { useState, useCallback } from "react";

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface UseFieldMovementOptions {
  scale: number;
  /** Return the rect for a given field ID, or null if not found. */
  getFieldRect: (fieldId: string) => Rect | null;
  /** Called when a field (or group of fields) should move to new rects. */
  onMoveFields: (updates: Array<{ id: string; rect: Rect }>) => void;
  /** Called when a single field is resized. */
  onResizeField: (fieldId: string, rect: Rect) => void;
  /** Set of currently selected field IDs (for group drag). Pass empty set if single-select only. */
  selectedFieldIds: Set<string>;
  /**
   * Optional alignment snapping: given the rect a drag/resize would commit,
   * return the (possibly adjusted) rect to commit instead. For group drags
   * it is applied to the leader and the same delta moves the group.
   */
  snapRect?: (fieldId: string, rect: Rect) => Rect;
  /** Called with the dragged field's current rect on every drag frame (e.g.
   *  to preview alignment guides). */
  onLiveDrag?: (fieldId: string, rect: Rect) => void;
  /** Called when a drag or resize ends, before the change commits. */
  onDragEnd?: () => void;
  /**
   * Optional resize snapping: given the rect a resize would commit and the
   * react-rnd direction (e.g. "right", "bottomLeft"), return the adjusted
   * rect. Takes precedence over snapRect for resizes.
   */
  snapResizeRect?: (fieldId: string, rect: Rect, dir: string) => Rect;
  /** Called with the field's current rect on every resize frame. */
  onLiveResize?: (fieldId: string, rect: Rect, dir: string) => void;
}

interface UseFieldMovementResult {
  /** Live visual offset for group-dragged fields (non-leader fields). Null when not dragging. */
  groupDragDelta: { dx: number; dy: number } | null;
  /** The field ID driving the current group drag. Null when not dragging. */
  groupDragFieldId: string | null;
  /** Returns Rnd drag/resize event handlers for a given field. */
  getRndHandlers: (fieldId: string) => {
    onDragStart: () => void;
    onDrag: (e: any, d: { x: number; y: number }) => void;
    onDragStop: (e: any, d: { x: number; y: number }) => void;
    onResize: (e: any, dir: any, ref: HTMLElement, delta: any, pos: { x: number; y: number }) => void;
    onResizeStop: (e: any, dir: any, ref: HTMLElement, delta: any, pos: { x: number; y: number }) => void;
  };
  /** Compute the visual position for a field, accounting for group drag delta. */
  getFieldPosition: (fieldId: string, rect: Rect) => { x: number; y: number };
}

export function useFieldMovement({
  scale,
  getFieldRect,
  onMoveFields,
  onResizeField,
  selectedFieldIds,
  snapRect,
  onLiveDrag,
  onDragEnd,
  snapResizeRect,
  onLiveResize,
}: UseFieldMovementOptions): UseFieldMovementResult {
  const [groupDragDelta, setGroupDragDelta] = useState<{ dx: number; dy: number } | null>(null);
  const [groupDragFieldId, setGroupDragFieldId] = useState<string | null>(null);

  const getRndHandlers = useCallback(
    (fieldId: string) => ({
      onDragStart: () => {
        if (selectedFieldIds.has(fieldId) && selectedFieldIds.size > 1) {
          setGroupDragFieldId(fieldId);
          setGroupDragDelta({ dx: 0, dy: 0 });
        }
      },
      onDrag: (_e: any, d: { x: number; y: number }) => {
        const rect = getFieldRect(fieldId);
        if (rect && onLiveDrag) {
          onLiveDrag(fieldId, { ...rect, x: d.x / scale, y: d.y / scale });
        }
        if (groupDragFieldId === fieldId) {
          if (rect) {
            setGroupDragDelta({
              dx: d.x - rect.x * scale,
              dy: d.y - rect.y * scale,
            });
          }
        }
      },
      onDragStop: (_e: any, d: { x: number; y: number }) => {
        onDragEnd?.();
        setGroupDragFieldId(null);
        setGroupDragDelta(null);

        const rect = getFieldRect(fieldId);
        if (!rect) return;

        let moved: Rect = { ...rect, x: d.x / scale, y: d.y / scale };
        if (snapRect) moved = snapRect(fieldId, moved);
        const dx = moved.x - rect.x;
        const dy = moved.y - rect.y;

        // Group move: apply same delta to all selected fields
        if (selectedFieldIds.has(fieldId) && selectedFieldIds.size > 1) {
          const updates: Array<{ id: string; rect: Rect }> = [];
          selectedFieldIds.forEach((id) => {
            const r = getFieldRect(id);
            if (r) {
              updates.push({
                id,
                rect: { ...r, x: r.x + dx, y: r.y + dy },
              });
            }
          });
          onMoveFields(updates);
        } else {
          onMoveFields([{ id: fieldId, rect: moved }]);
        }
      },
      onResize: (
        _e: any,
        dir: any,
        ref: HTMLElement,
        _delta: any,
        pos: { x: number; y: number },
      ) => {
        if (!onLiveResize) return;
        onLiveResize(
          fieldId,
          {
            x: pos.x / scale,
            y: pos.y / scale,
            width: ref.offsetWidth / scale,
            height: ref.offsetHeight / scale,
          },
          String(dir),
        );
      },
      onResizeStop: (
        _e: any,
        dir: any,
        ref: HTMLElement,
        _delta: any,
        pos: { x: number; y: number },
      ) => {
        onDragEnd?.();
        let resized: Rect = {
          x: pos.x / scale,
          y: pos.y / scale,
          width: ref.offsetWidth / scale,
          height: ref.offsetHeight / scale,
        };
        if (snapResizeRect) resized = snapResizeRect(fieldId, resized, String(dir));
        onResizeField(fieldId, resized);
      },
    }),
    [scale, getFieldRect, onMoveFields, onResizeField, selectedFieldIds, groupDragFieldId, snapRect, onLiveDrag, onDragEnd, snapResizeRect, onLiveResize],
  );

  const getFieldPosition = useCallback(
    (fieldId: string, rect: Rect) => {
      const isGroupDragging =
        groupDragDelta &&
        groupDragFieldId &&
        groupDragFieldId !== fieldId &&
        selectedFieldIds.has(fieldId);

      return {
        x: rect.x * scale + (isGroupDragging ? groupDragDelta.dx : 0),
        y: rect.y * scale + (isGroupDragging ? groupDragDelta.dy : 0),
      };
    },
    [scale, groupDragDelta, groupDragFieldId, selectedFieldIds],
  );

  return {
    groupDragDelta,
    groupDragFieldId,
    getRndHandlers,
    getFieldPosition,
  };
}
